import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getCampanias,
  getCampania,
  crearCampania,
  actualizarCampania,
  eliminarCampania,
  toggleCampania,
  descargarQR,
  getUsuariosTonal,
} from '../api/muestrasApi'
import type { CampaniaCreatePayload, CampaniaUpdatePayload } from '../types'

export const campaniasKeys = {
  all: ['muestras-campanias'] as const,
  list: (params?: Record<string, unknown>) => [...campaniasKeys.all, 'list', params] as const,
  detail: (id: number) => [...campaniasKeys.all, 'detail', id] as const,
  usuariosTonal: ['usuarios-tonal'] as const,
}

export function useCampanias(params?: { page?: number; page_size?: number; search?: string; grupo_laboratorio_id?: number }) {
  return useQuery({
    queryKey: campaniasKeys.list(params),
    queryFn: () => getCampanias(params),
  })
}

export function useCampania(id: number) {
  return useQuery({
    queryKey: campaniasKeys.detail(id),
    queryFn: () => getCampania(id),
    enabled: id > 0,
  })
}

export function useCrearCampania() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CampaniaCreatePayload) => crearCampania(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaniasKeys.all })
      toast.success('Campaña creada correctamente')
    },
    onError: () => toast.error('Error al crear campaña'),
  })
}

export function useActualizarCampania() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CampaniaUpdatePayload }) =>
      actualizarCampania(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: campaniasKeys.all })
      queryClient.invalidateQueries({ queryKey: campaniasKeys.detail(id) })
      toast.success('Campaña actualizada')
    },
    onError: () => toast.error('Error al actualizar campaña'),
  })
}

export function useEliminarCampania() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarCampania(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaniasKeys.all })
      toast.success('Campaña eliminada')
    },
    onError: () => toast.error('Error al eliminar campaña'),
  })
}

export function useToggleCampania() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => toggleCampania(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaniasKeys.all })
    },
  })
}

export function useDescargarQR() {
  return useMutation({
    mutationFn: (id: number) => descargarQR(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'qr-campania.png'
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al descargar QR'),
  })
}

export function useUsuariosTonal() {
  return useQuery({
    queryKey: campaniasKeys.usuariosTonal,
    queryFn: getUsuariosTonal,
  })
}
